import { iconAlertaAtencao, iconAlertaCritico, iconAlertaSucesso, iconDoc2aVia, iconNavBusca, iconNavFiltro } from '@bsds-lib-component/icons/icons';
import { BsdsCard, BsdsIcon, BsdsInput, BsdsModal } from '@bsds-lib-component/react';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { IoTrendingUpOutline } from "react-icons/io5";

import type { InvoiceHistoryItem } from '../../../../components';
import { Button, Dropdown, InvoiceHistoryTable, SystemMessage, useSystemContainer, Visibility } from '../../../../components';
import { useVidaPayment } from '../../../../hooks';
import { useSystemMessage } from '../../../../hooks/useSystemMessage';
import { useCompanyStore } from '../../../../store/companyStore';
import { hasSelectedCompanyPolicyNumber } from '../../../../types/companyType';
import type { Invoice } from '../../../../types/vida/payment/paymentType';
import { generateInvoicesXLSX, VIDA_INVOICE_EXCEL_COLUMNS } from '../../../../utils/invoiceExcelUtils';
import { generateInvoicePDF, VIDA_INVOICE_PDF_LABELS } from '../../../../utils/invoicePdfUtils';
import { runTabMountSequence } from '../../../../utils/tabLoadTiming';
import { subestipulanteDropdownOptions as buildSubestipulanteDropdownOptions, formatSubestipulanteCodNome } from '../../../../utils/subestipulanteUtil';

const DetailRow: React.FC<{ label: string; value: string; ariaLabel: string }> = ({ label, value, ariaLabel }) => (
  <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center py-2 border-b border-gray-50">
    <span className="text-sm text-text-color">{label}:</span>
    <span className="text-sm font-normal" aria-label={`${ariaLabel}: ${value}`}>
      {value}
    </span>
  </div>
);

const PAYMENT_LIST_PAGE_SIZE = 6;

type PanelFilters = {
  identContrato: string;
  identSubestipulante: string;
  tipoFatura: string;
  idEndosso: string;
  codFormaCobranca: string;
  origemFront: string;
  pSitFatura: string;
};

const matchesPanelFilters = (invoice: Invoice, filters: PanelFilters): boolean => {
  if (filters.identContrato && invoice.policyNumber !== filters.identContrato) {
    return false;
  }
  if (filters.identSubestipulante && String(invoice.identSubestipulante ?? '') !== filters.identSubestipulante) {
    return false;
  }
  if (filters.tipoFatura) {
    const term = filters.tipoFatura.toLowerCase();
    const typeMatch = invoice.type?.toLowerCase().includes(term);
    const codigoMatch = invoice.tipoFaturaCodigo?.toLowerCase().includes(term);
    if (!typeMatch && !codigoMatch) return false;
  }
  if (filters.idEndosso && String(invoice.identEndosso ?? '') !== filters.idEndosso) {
    return false;
  }
  if (filters.codFormaCobranca && String(invoice.codFormaCobranca ?? '') !== filters.codFormaCobranca) {
    return false;
  }
  if (filters.pSitFatura) {
    const term = filters.pSitFatura.toLowerCase();
    const statusMatch = invoice.status?.toLowerCase().includes(term);
    const situacaoMatch = invoice.situacaoFatura?.toLowerCase().includes(term);
    if (!statusMatch && !situacaoMatch) return false;
  }
  return true;
};

const PaymentTab: React.FC = () => {
  const {
    summary,
    invoices,
    isLoading,
    selectedInvoice,
    selectInvoice,
    closeModal,
    getPaymentInvoiceHistory,
    getPaymentInvoiceHistoryByCompetencias,
    loadPaymentInvoiceCompetencias,
    getPaymentInvoiceCompetenciaFaturas,
    applyCompetenciaSubestipulanteFilter,
    competenciaDropdownOptions,
  } = useVidaPayment();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  // Filter states
  const [identContrato, setIdentContrato] = useState<string>('');
  const [competenciaFatura, setCompetenciaFatura] = useState<string>('');
  const [quickIdentSubestipulante, setQuickIdentSubestipulante] = useState<string>('');
  const [identSubestipulante, setIdentSubestipulante] = useState<string>('');
  const [tipoFatura, setTipoFatura] = useState<string>('');
  const [idEndosso, setIdEndosso] = useState<string>('');
  const [codFormaCobranca, setCodFormaCobranca] = useState<string>('');
  const [origemFront, setOrigemFront] = useState<string>('');
  const [pSitFatura, setPSitFatura] = useState<string>('');
  const [appliedPanelFilters, setAppliedPanelFilters] = useState<PanelFilters | null>(null);
  const [clientPage, setClientPage] = useState(1);
  const [tabMountBusy, setTabMountBusy] = useState(false);
  const didInitialAutoFetchRef = useRef(false);
  const selectedCompany = useCompanyStore((s) => s.selectedCompany);
  const { setLoading } = useSystemContainer();
  const { showMessage, message, type, show } = useSystemMessage();
  const mainContentId = useId();
  const messageId = useId();
  const summaryCardsId = useId();
  const invoicesSectionId = useId();
  const searchInputId = useId();
  const filtersPanelId = useId();
  const quickCompetenciaSelectId = useId();
  const modalId = useId();

  const handleInvoiceClick = async (invoice: InvoiceHistoryItem) => {
    setLoading(true);

    try {
      // Convert InvoiceHistoryItem to Invoice format
      const invoiceForSelection: typeof invoices[0] = {
        id: String(invoice.id),
        invoiceMonth: invoice.invoiceMonth || invoice.billingMonth || '',
        policyNumber: invoice.policyNumber || invoice.contractNumber || '',
        type: invoice.type,
        dueDate: invoice.dueDate,
        amount: invoice.amount,
        status: invoice.status as 'Pago' | 'Pendente' | 'Vencido' | 'Cancelado',
      };

      // Promise.all to ensure minimum time of 1500ms and make the request
      await Promise.all([
        selectInvoice(invoiceForSelection),
        new Promise(resolve => setTimeout(resolve, 100))
      ]);

      // If we got here, the request was successful
      setIsModalOpen(true);
    } catch (err) {
      console.error('Error loading invoice detail:', err);
      // Shows error message and does not open modal
      showMessage('Não foi possível carregar os detalhes da fatura. Tente novamente.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    closeModal();
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  /** Format string value (ex.: "408085.17") to currency. */
  const formatCurrencyFromString = (value: string) => {
    const num = parseFloat(value || '0') || 0;
    return formatCurrency(num);
  };

  /** Format date dd-MM-yyyy to dd/MM/yyyy. */
  const formatDateDdMmYyyy = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '—';
    const parts = dateStr.split('-');
    if (parts.length >= 3) {
      const [d, m, y] = parts;
      return `${d}/${m}/${y}`;
    }
    return dateStr;
  };

  const getStatusBadgeClass = (status: string, origin?: string) => {
    switch (status) {
      case 'Pago':
        return origin === 'table' ? 'bg-green-100 text-green-800' : 'text-green-800';
      case 'Pendente':
        return origin === 'table' ? 'bg-yellow-100 text-yellow-800' : 'text-yellow-800';
      case 'Vencido':
        return origin === 'table' ? 'bg-red-100 text-red-800' : 'text-red-800';
      case 'Cancelado':
        return origin === 'table' ? 'bg-gray-100 text-gray-800' : 'text-gray-800';
      case 'Em dia':
        return origin === 'table' ? 'bg-green-100 text-green-600' : 'text-green-600';
      case 'Atenção':
        return origin === 'table' ? 'bg-yellow-100 text-yellow-800' : 'text-yellow-800';
      case 'Atrasado':
        return origin === 'table' ? 'bg-red-100 text-red-800' : 'text-red-800';
      default:
        return origin === 'table' ? 'bg-gray-100 text-gray-800' : 'text-gray-800';
    }
  };

  // Filter and sort invoices (client-side filtering for panel filters, search and sort)
  const filteredAndSortedInvoices = useMemo(() => {
    let filtered = invoices;

    if (appliedPanelFilters) {
      filtered = filtered.filter((invoice) => matchesPanelFilters(invoice, appliedPanelFilters));
    }

    if (searchTerm && searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((invoice) => {
        const monthMatch = invoice.invoiceMonth?.toLowerCase().includes(term);
        const policyMatch = invoice.policyNumber?.toLowerCase().includes(term);
        const subestipulanteMatch = formatSubestipulanteCodNome(
          invoice.codSubestipulante,
          invoice.nomeSubestipulante,
        ).toLowerCase().includes(term);
        const typeMatch = invoice.type?.toLowerCase().includes(term);
        const statusMatch = invoice.status?.toLowerCase().includes(term);
        const dueDateMatch = invoice.dueDate?.toLowerCase().includes(term);
        const amountMatch = formatCurrency(invoice.amount).toLowerCase().includes(term);

        return monthMatch || policyMatch || subestipulanteMatch || typeMatch || statusMatch || dueDateMatch || amountMatch;
      });
    }

    if (sortBy) {
      filtered = [...filtered].sort((a, b) => {
        switch (sortBy) {
          case 'invoiceMonth':
            return a.invoiceMonth.localeCompare(b.invoiceMonth);
          case 'invoiceMonth-desc':
            return b.invoiceMonth.localeCompare(a.invoiceMonth);
          case 'policyNumber':
            return a.policyNumber.localeCompare(b.policyNumber);
          case 'policyNumber-desc':
            return b.policyNumber.localeCompare(a.policyNumber);
          case 'type':
            return a.type.localeCompare(b.type);
          case 'type-desc':
            return b.type.localeCompare(a.type);
          case 'dueDate':
            return a.dueDate.localeCompare(b.dueDate);
          case 'dueDate-desc':
            return b.dueDate.localeCompare(a.dueDate);
          case 'amount':
            return a.amount - b.amount;
          case 'amount-desc':
            return b.amount - a.amount;
          case 'status':
            return a.status.localeCompare(b.status);
          case 'status-desc':
            return b.status.localeCompare(a.status);
          default:
            return 0;
        }
      });
    }

    return filtered;
  }, [invoices, appliedPanelFilters, searchTerm, sortBy]);

  const clientTotalElements = filteredAndSortedInvoices.length;
  const clientTotalPages = Math.max(1, Math.ceil(clientTotalElements / PAYMENT_LIST_PAGE_SIZE));

  const paginatedInvoices = useMemo(() => {
    const start = (clientPage - 1) * PAYMENT_LIST_PAGE_SIZE;
    return filteredAndSortedInvoices.slice(start, start + PAYMENT_LIST_PAGE_SIZE);
  }, [filteredAndSortedInvoices, clientPage]);

  const monthlyAverageAmount = useMemo(() => {
    if (filteredAndSortedInvoices.length === 0) return 0;
    const total = filteredAndSortedInvoices.reduce((sum, invoice) => sum + (invoice.amount ?? 0), 0);
    return total / filteredAndSortedInvoices.length;
  }, [filteredAndSortedInvoices]);

  useEffect(() => {
    setClientPage(1);
  }, [invoices, appliedPanelFilters, searchTerm, sortBy]);

  const subestipulanteDropdownOptions = useMemo(
    () => buildSubestipulanteDropdownOptions(selectedCompany?.subestipulantes, false),
    [selectedCompany?.subestipulantes],
  );

  useEffect(() => {
    const allowedSubestipulantes = new Set(subestipulanteDropdownOptions.map((o) => o.value));
    if (quickIdentSubestipulante && !allowedSubestipulantes.has(quickIdentSubestipulante)) {
      setQuickIdentSubestipulante('');
    }
    if (identSubestipulante && !allowedSubestipulantes.has(identSubestipulante)) {
      setIdentSubestipulante('');
    }
  }, [subestipulanteDropdownOptions, quickIdentSubestipulante, identSubestipulante]);

  useEffect(() => {
    const allowedCompetencias = new Set(competenciaDropdownOptions.map((o) => o.value));
    if (competenciaFatura && !allowedCompetencias.has(competenciaFatura)) {
      setCompetenciaFatura('');
    }
  }, [competenciaDropdownOptions, competenciaFatura]);

  const handleQuickSubestipulanteChange = (value: string) => {
    setQuickIdentSubestipulante(value);
    applyCompetenciaSubestipulanteFilter(
      value ? parseInt(value, 10) : undefined
    );
  };

  const handlePageChange = (page: number) => {
    setClientPage(page);
  };

  const handleExportInvoices = async (selectedInvoices: InvoiceHistoryItem[]) => {
    await generateInvoicesXLSX(
      selectedInvoices,
      formatCurrency,
      (dateStr: string) => dateStr,
      undefined,
      VIDA_INVOICE_EXCEL_COLUMNS,
    );
  };

  const handleExportInvoicePdf = async (invoice: InvoiceHistoryItem) => {
    await generateInvoicePDF(
      invoice,
      formatCurrency,
      (dateStr: string) => dateStr,
      undefined,
      VIDA_INVOICE_PDF_LABELS,
    );
  };

  const handleApplyFilters = () => {
    if (!isFilterButtonEnabled) return;
    setAppliedPanelFilters({
      identContrato,
      identSubestipulante,
      tipoFatura,
      idEndosso,
      codFormaCobranca,
      origemFront,
      pSitFatura,
    });
    setShowFilters(false);
  };

  const isCompetenciaValid = useMemo(() => /^\d{2}-\d{4}$/.test(competenciaFatura), [competenciaFatura]);

  const handleQuickSearch = async () => {
    if (!competenciaFatura || !/^\d{2}-\d{4}$/.test(competenciaFatura)) {
      showMessage('Selecione a competência (mm/aaaa).', 'warning');
      return;
    }

    const identContratoApol = selectedCompany?.apoliceContext?.identContratoApol;
    if (identContratoApol == null || identContratoApol <= 0) {
      showMessage('Não foi possível identificar o contrato da apólice para pesquisa.', 'warning');
      return;
    }

    try {
      await getPaymentInvoiceHistory({
        identContrato: identContratoApol,
        competenciaFatura,
        identSubestipulante: quickIdentSubestipulante ? parseInt(quickIdentSubestipulante, 10) : undefined,
        page: 0,
      });
      setAppliedPanelFilters(null);
      setClientPage(1);
    } catch (err) {
      console.error('Error performing quick payment search:', err);
      showMessage('Não foi possível realizar a busca. Tente novamente.', 'error');
    }
  };

  const isFilterButtonEnabled = useMemo(() => invoices.length > 0, [invoices.length]);

  // identContrato from policy list (CompanyPage / link flow) for lista-faturas GET
  React.useEffect(() => {
    if (!hasSelectedCompanyPolicyNumber(selectedCompany)) return;
    const ctx = selectedCompany?.apoliceContext;
    if (!ctx) return;
    const contractId = ctx.identContratoApol ?? ctx.identContrato ?? undefined;
    if (contractId != null && contractId > 0) {
      setIdentContrato(String(contractId));
    }
  }, [selectedCompany, selectedCompany?.id, selectedCompany?.apoliceContext]);

  // New company selected: allows new automatic loading when entering the tab
  React.useEffect(() => {
    didInitialAutoFetchRef.current = false;
    setQuickIdentSubestipulante('');
    setCompetenciaFatura('');
    setAppliedPanelFilters(null);
    setClientPage(1);
    applyCompetenciaSubestipulanteFilter(undefined);
  }, [selectedCompany?.id, applyCompetenciaSubestipulanteFilter]);

  // First load: after tab delay, list invoices when apólice + contract context are available
  React.useEffect(() => {
    if (didInitialAutoFetchRef.current) return;
    if (!hasSelectedCompanyPolicyNumber(selectedCompany)) return;
    const identContratoApol = selectedCompany?.apoliceContext?.identContratoApol;
    if (identContratoApol == null || identContratoApol <= 0) return;

    let cancelled = false;
    (async () => {
      setTabMountBusy(true);
      try {
        await runTabMountSequence(async () => {
          if (cancelled) return;
          try {
            await loadPaymentInvoiceCompetencias({ identContrato: identContratoApol });

            const competenciasFatura = await getPaymentInvoiceCompetenciaFaturas({
              identContrato: identContratoApol,
            });

            if (competenciasFatura.length > 0) {
              await getPaymentInvoiceHistoryByCompetencias({
                identContrato: identContratoApol,
                competenciasFatura,
                page: 0,
              });
            }

            didInitialAutoFetchRef.current = true;
          } catch (err) {
            console.error('Erro ao carregar histórico de pagamentos (carga inicial):', err);
            showMessage('Não foi possível carregar os dados de pagamento. Tente novamente.', 'error');
          }
        });
      } finally {
        if (!cancelled) setTabMountBusy(false);
      }
    })();
    return () => {
      cancelled = true;
      setTabMountBusy(false);
    };
  }, [
    selectedCompany,
    selectedCompany?.apoliceContext?.identContratoApol,
    loadPaymentInvoiceCompetencias,
    getPaymentInvoiceCompetenciaFaturas,
    getPaymentInvoiceHistoryByCompetencias,
    showMessage,
  ]);

  // Sync loading state with SystemContainer
  React.useEffect(() => {
    setLoading(tabMountBusy || isLoading);
  }, [tabMountBusy, isLoading, setLoading]);

  return (
    <div
      className="bsds-width-100 bsds-mt-12 bsds-p-8"
      role="main"
      aria-busy={tabMountBusy || isLoading}
      aria-label="Página de pagamento de apólice"
      id={mainContentId}
    >
      {show && (
        <div
          id={messageId}
          className="bsds-mt-16"
          role={type === 'error' ? 'alert' : 'status'}
          aria-live={type === 'error' ? 'assertive' : 'polite'}
          aria-atomic="true"
        >
          <SystemMessage
            message={message}
            type={type}
            show={show}
          />
        </div>
      )}
      <div className="bsds-display-flex bsds-justify-content-start bsds-align-items-center">
        <div className="bsds-display-flex bsds-align-items-center">
          <div className="w-[2.5px] h-8  bg-red-600 bsds-mr-8" aria-hidden="true" />
        </div>
        <h5 className="bsds-ml-8 font-normal">
          Pesquisa de Pagamentos da Apólice
        </h5>
      </div>
      <div className="bsds-mt-16 bsds-mb-16" role="region" aria-label="Busca rápida por competência e subestipulante">
        <div className="flex flex-col md:flex-row gap-4 w-full">
          <div className="w-full md:w-1/3 md:flex-none">
            <Dropdown
              id={quickCompetenciaSelectId}
              data-testid="payment-dropdown-quick-competencia"
              placeholder="mm/aaaa"
              label="Competência *"
              required
              value={competenciaFatura}
              onChange={setCompetenciaFatura}
              options={competenciaDropdownOptions}
            />
          </div>
          <div className="w-full md:flex-1 min-w-0">
            <Dropdown
              id="quick-subestipulante"
              data-testid="payment-dropdown-quick-subestipulante"
              placeholder="Selecione um Sub-Estipulante"
              label="Sub-Estipulante"
              value={quickIdentSubestipulante}
              onChange={handleQuickSubestipulanteChange}
              options={subestipulanteDropdownOptions}
            />
          </div>
          <div className={`bsds-display-flex bsds-align-items-end bsds-mt-${!competenciaFatura ? '24' : '4'} ${!competenciaFatura ? 'bsds-mb-24' : 'bsds-mb-4'}`}>
            <Button
              type="button"
              color="primary"
              fill="solid"
              icon={iconNavBusca}
              iconPosition="start"
              className="w-full sm:w-auto"
              disabled={!isCompetenciaValid}
              onClick={() => handleQuickSearch()}
              aria-label="Pesquisar pagamentos por competência e subestipulante"
            >
              Pesquisar
            </Button>
          </div>
        </div>
      </div>
      {/* Info Cards */}
      <div
        id={summaryCardsId}
        className="mb-6 bsds-mt-24 flex justify-center"
        role="region"
        aria-label="Resumo de informações do pagamento"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-4 items-stretch w-full justify-items-center md:justify-items-center max-w-[1000px]">
          {/* Monthly Average Card */}
          <BsdsCard className="p-8 w-full sm:w-full md:w-full lg:w-full xl:w-[260px]" platform="web" role="region" aria-label="Média mensal das faturas exibidas">
            <div className="flex justify-between items-center h-full">
              <div className="flex flex-col flex-1">
                <span className="text-xs text-text-color mb-2 uppercase">Média Mensal</span>
                <span className="text-md font-bold text-green-600" aria-label={`Média mensal: ${formatCurrency(monthlyAverageAmount)}`}>
                  {formatCurrency(monthlyAverageAmount)}
                </span>
              </div>
              <IoTrendingUpOutline className="w-5 h-5 text-green-600" aria-hidden="true" />
            </div>
          </BsdsCard>

          {/* Status Card */}
          <BsdsCard className="p-8 w-full sm:w-full md:w-full lg:w-full xl:w-[260px]" platform="web" role="region" aria-label="Status do pagamento">
            <div className="flex justify-between items-center h-full">
              <div className="flex flex-col flex-1">
                <span className="text-xs text-text-color mb-2 uppercase">Status</span>
                <span className={`text-md font-bold ${getStatusBadgeClass(summary.status)}`} aria-label={`Status: ${summary.status}`}>
                  {summary.status}
                </span>
              </div>
              {summary.status === 'Em dia' ? (
                <BsdsIcon icon={iconAlertaSucesso} className="w-5 h-5 text-green-600" aria-hidden="true" />
              ) : (
                <BsdsIcon icon={iconAlertaCritico} className="w-5 h-5 text-red-400" aria-hidden="true" />
              )}
            </div>
          </BsdsCard>
        </div>
      </div>

      {/* Invoices Table */}
      <div
        id={invoicesSectionId}
        className="bsds-mt-16"
        role="region"
        aria-label="Histórico de pagamentos"
      >
        <div className="bsds-display-flex bsds-justify-content-start bsds-align-items-center">
          <BsdsIcon icon={iconDoc2aVia} className="w-4 h-4" aria-hidden="true" />
          <h6 className="bsds-ml-8 font-semibold">Histórico de Pagamentos</h6>
        </div>

        {/* Search, Sort and Filters */}
        <div className="bsds-mb-16 bsds-mt-16">
          <div
            className="flex flex-col sm:flex-row gap-4 w-full"
            role="group"
            aria-label="Ferramentas de busca, ordenação e filtros"
          >
            {/* Search field */}
            <div className="flex-1">
              <BsdsInput
                id={searchInputId}
                type="text"
                clearInput={false}
                placeholder="Filtre por Sub-Estipulante, movimentação, vencimento, status, etc."
                value={searchTerm}
                onInput={(e) => {
                  const target = e.target as HTMLBsdsInputElement;
                  const value = (e as React.FormEvent<HTMLBsdsInputElement> & { detail?: { value: string } }).detail?.value || target?.value || '';
                  setSearchTerm(String(value));
                }}
                platform="web"
                aria-label="Campo de busca no histórico de faturas"
                aria-describedby={`${searchInputId}-help`}
              />
              <span
                id={`${searchInputId}-help`}
                className="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0"
                style={{ clip: 'rect(0, 0, 0, 0)', clipPath: 'inset(50%)' }}
              >
                Digite termos para filtrar por mês, apólice, tipo, status, vencimento ou valor
              </span>
            </div>

            {/* Sort dropdown */}
            <div className="w-full sm:w-60">
              <Dropdown
                id="sort-invoices"
                data-testid="payment-dropdown-sort-invoices"
                placeholder="Ordenar por..."
                value={sortBy}
                onChange={setSortBy}
                options={[
                  { label: 'Mês da Fatura (A-Z)', value: 'invoiceMonth' },
                  { label: 'Mês da Fatura (Z-A)', value: 'invoiceMonth-desc' },
                  { label: 'Número Apólice (A-Z)', value: 'policyNumber' },
                  { label: 'Número Apólice (Z-A)', value: 'policyNumber-desc' },
                  { label: 'Tipo (A-Z)', value: 'type' },
                  { label: 'Tipo (Z-A)', value: 'type-desc' },
                  { label: 'Vencimento (A-Z)', value: 'dueDate' },
                  { label: 'Vencimento (Z-A)', value: 'dueDate-desc' },
                  { label: 'Valor (Menor)', value: 'amount' },
                  { label: 'Valor (Maior)', value: 'amount-desc' },
                  { label: 'Status (A-Z)', value: 'status' },
                  { label: 'Status (Z-A)', value: 'status-desc' },
                ]}
              />
            </div>

            {/* Filters button */}
            <Button
              type="button"
              color="primary"
              fill="outline"
              onClick={() => setShowFilters(!showFilters)}
              icon={iconNavFiltro}
              iconPosition="start"
              aria-expanded={showFilters}
              aria-controls={filtersPanelId}
              aria-label="Abrir painel de filtros"
            >
              Filtros
            </Button>
          </div>

          {/* Filters panel (expandable) */}
          <Visibility
            isActive={showFilters}
            animated
            animationDuration={300}
          >
            <div
              id={filtersPanelId}
              className="bsds-mt-16 bsds-width-100 bg-white rounded-lg shadow-sm bsds-p-24 border border-gray-100"
              role="region"
              aria-label="Configurações de filtros"
            >
              <div className="bsds-display-flex bsds-justify-content-start bsds-align-items-center bsds-mb-16">
                <BsdsIcon icon={iconNavFiltro} className="w-4 h-4" aria-hidden="true" />
                <h6 className="font-semibold bsds-ml-8">Filtros</h6>
              </div>
              <div>
                <div className="bsds-display-flex flex-col gap-4">
                  {/* Required field */}
                  <div className="bsds-display-flex flex-col sm:flex-row gap-4">
                    <div className="bsds-width-100 sm:bsds-flex-1">
                      <BsdsInput
                        type="number"
                        clearInput={false}
                        label="Identificador do Contrato"
                        value={identContrato}
                        onInput={(e) => {
                          const target = e.target as HTMLBsdsInputElement;
                          const value = (e as React.FormEvent<HTMLBsdsInputElement> & { detail?: { value: string } }).detail?.value || target?.value || '';
                          setIdentContrato(String(value));
                        }}
                        platform="web"
                      />
                    </div>
                  </div>

                  {/* Optional fields */}
                  <div className="bsds-display-flex flex-col sm:flex-row gap-4">
                    <div className="bsds-width-100 sm:bsds-flex-1">
                      <Dropdown
                        id="filter-subestipulante"
                        data-testid="payment-dropdown-filter-subestipulante"
                        placeholder="Sub-Estipulante (opcional)"
                        label="Sub-Estipulante"
                        value={identSubestipulante}
                        onChange={setIdentSubestipulante}
                        options={subestipulanteDropdownOptions}
                      />
                    </div>
                    <div className="bsds-width-100 sm:bsds-flex-1">
                      <BsdsInput
                        type="text"
                        clearInput={false}
                        label="Tipo de Fatura"
                        value={tipoFatura}
                        onInput={(e) => {
                          const target = e.target as HTMLBsdsInputElement;
                          const value = (e as React.FormEvent<HTMLBsdsInputElement> & { detail?: { value: string } }).detail?.value || target?.value || '';
                          setTipoFatura(String(value));
                        }}
                        platform="web"
                      />
                    </div>
                  </div>

                  <div className="bsds-display-flex flex-col sm:flex-row gap-4">
                    <div className="bsds-width-100 sm:bsds-flex-1">
                      <BsdsInput
                        type="number"
                        clearInput={false}
                        label="Identificador de Endosso"
                        value={idEndosso}
                        onInput={(e) => {
                          const target = e.target as HTMLBsdsInputElement;
                          const value = (e as React.FormEvent<HTMLBsdsInputElement> & { detail?: { value: string } }).detail?.value || target?.value || '';
                          setIdEndosso(String(value));
                        }}
                        platform="web"
                      />
                    </div>
                    <div className="bsds-width-100 sm:bsds-flex-1">
                      <BsdsInput
                        type="number"
                        clearInput={false}
                        label="Código de Forma de Cobrança"
                        value={codFormaCobranca}
                        onInput={(e) => {
                          const target = e.target as HTMLBsdsInputElement;
                          const value = (e as React.FormEvent<HTMLBsdsInputElement> & { detail?: { value: string } }).detail?.value || target?.value || '';
                          setCodFormaCobranca(String(value));
                        }}
                        platform="web"
                      />
                    </div>
                  </div>

                  <div className="bsds-display-flex flex-col sm:flex-row gap-4">
                    <div className="bsds-width-100 sm:bsds-flex-1">
                      <BsdsInput
                        type="text"
                        clearInput={false}
                        label="Origem Front"
                        value={origemFront}
                        onInput={(e) => {
                          const target = e.target as HTMLBsdsInputElement;
                          const value = (e as React.FormEvent<HTMLBsdsInputElement> & { detail?: { value: string } }).detail?.value || target?.value || '';
                          setOrigemFront(String(value));
                        }}
                        platform="web"
                      />
                    </div>
                    <div className="bsds-width-100 sm:bsds-flex-1">
                      <BsdsInput
                        type="text"
                        clearInput={false}
                        label="Status da Fatura"
                        placeholder=""
                        value={pSitFatura}
                        onInput={(e) => {
                          const target = e.target as HTMLBsdsInputElement;
                          const value = (e as React.FormEvent<HTMLBsdsInputElement> & { detail?: { value: string } }).detail?.value || target?.value || '';
                          setPSitFatura(String(value));
                        }}
                        platform="web"
                      />
                    </div>
                  </div>

                  <div className="bsds-display-flex bsds-align-items-end bsds-width-100 sm:bsds-width-auto">
                    <Button
                      type="button"
                      color="primary"
                      fill="solid"
                      disabled={!isFilterButtonEnabled}
                      className="bsds-width-100 mb-2 sm:bsds-width-100"
                      onClick={() => handleApplyFilters()}
                      aria-label={
                        !isFilterButtonEnabled
                          ? 'Realize a busca rápida para carregar faturas antes de filtrar'
                          : 'Aplicar filtros na listagem'
                      }
                    >
                      Filtrar
                    </Button>
                  </div>
                </div>
                {!isFilterButtonEnabled && (
                  <div
                    className="bsds-mt-16 bsds-display-flex bsds-align-items-center border-1 border-orange-300 bg-orange-100 rounded-md bsds-p-8"
                    role="alert"
                    aria-live="assertive"
                  >
                    <BsdsIcon className="w-5 h-5 -600 text-orange-600 bsds-mr-8" icon={iconAlertaAtencao} aria-hidden="true" />
                    <span className="bsds-text-center text-sm text-white font-semibold">
                      Realize a busca rápida para carregar as faturas antes de aplicar os filtros.
                    </span>
                  </div>
                )}
              </div>
            </div>
          </Visibility>
        </div>
        <BsdsCard className="bsds-p-16">
          <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
            <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
            <h4 className="opacity-80">Histórico de Pagamentos</h4>
          </div>
          <InvoiceHistoryTable
            invoices={paginatedInvoices as InvoiceHistoryItem[]}
            formatCurrency={formatCurrency}
            formatDate={(dateStr: string) => dateStr}
            getStatusBadgeClass={getStatusBadgeClass}
            onRowClick={handleInvoiceClick}
            onExport={handleExportInvoices}
            onExportPdf={handleExportInvoicePdf}
            currentPage={clientPage}
            totalPages={clientTotalPages}
            totalItems={!isLoading ? clientTotalElements : 0}
            onPageChange={handlePageChange}
          />
        </BsdsCard>
      </div>

      {/* Detail Modal */}
      <BsdsModal
        isOpen={isModalOpen}
        onBsdsModalDidDismiss={handleCloseModal}
        backdropDismiss
        color="secondary"
        canDismiss
        animated
        showBackdrop
        keepContentsMounted
        platform="web"
        aria-labelledby={modalId}
        aria-describedby={`${modalId}-description`}
      >
        <div
          className="modal-content flex flex-col h-screen max-h-screen md:h-[75vh]"
          role="dialog"
          aria-modal="true"
          aria-labelledby={modalId}
        >
          {selectedInvoice && (
            <>
              {/* Header fixed */}
              <div className="flex items-center gap-4 p-4 pb-3 flex-shrink-0 border-b border-gray-100">
                <BsdsIcon icon={iconDoc2aVia} color="secondary" className="icon-title" aria-hidden="true" />
                <h6 id={modalId} className="text-lg font-semibold text-text-color">Detalhamento da Fatura</h6>
              </div>

              {/* Scroll content - only fields returned by the API (obter-dados-fatura) */}
              <div
                id={`${modalId}-description`}
                className="overflow-y-auto custom-scrollbar flex-1 px-4 py-4 min-h-0 pb-38"
                role="region"
                aria-label="Detalhes da fatura"
              >
                <div className="flex flex-col gap-4">
                  <DetailRow label="Ident. Cobrança" value={String(selectedInvoice.identCobrancaObt)} ariaLabel="Identificador cobrança" />
                  <DetailRow label="Ident. Substipulante" value={String(selectedInvoice.identSubstipulanteObt)} ariaLabel="Identificador substipulante" />
                  <DetailRow label="Ident. Endosso" value={selectedInvoice.identEndossoObt != null ? String(selectedInvoice.identEndossoObt) : '—'} ariaLabel="Identificador endosso" />
                  <DetailRow label="Nº Parcela" value={String(selectedInvoice.numParcelaObt)} ariaLabel="Número parcela" />
                  <DetailRow label="Competência da Fatura" value={selectedInvoice.competenciaFaturaObt} ariaLabel="Competência fatura" />
                  <DetailRow label="Tipo de Fatura" value={String(selectedInvoice.tipoFaturaObt)} ariaLabel="Tipo fatura" />
                  <DetailRow label="Nosso Número" value={selectedInvoice.nossoNumFatura || '—'} ariaLabel="Nosso número" />
                  <DetailRow label="Situação" value={selectedInvoice.situacaoFaturaObt} ariaLabel="Situação fatura" />

                  <DetailRow label="Valor Importância Segurada" value={formatCurrencyFromString(selectedInvoice.valorImportSegurada)} ariaLabel="Valor importância segurada" />
                  <DetailRow label="Valor Prêmio Total" value={formatCurrencyFromString(selectedInvoice.valorPremioTotalObt)} ariaLabel="Valor prêmio total" />
                  <DetailRow label="Valor Prêmio Líquido" value={formatCurrencyFromString(selectedInvoice.valorPremioLiquidoObt)} ariaLabel="Valor prêmio líquido" />
                  <DetailRow label="Valor IOF" value={formatCurrencyFromString(selectedInvoice.valorIOF)} ariaLabel="Valor IOF" />
                  <DetailRow label="Valor Assistência Fatura" value={formatCurrencyFromString(selectedInvoice.valorAssistFatura)} ariaLabel="Valor assistência fatura" />
                  <DetailRow label="Valor Pago" value={formatCurrencyFromString(selectedInvoice.valorPagoFatura)} ariaLabel="Valor pago" />

                  <DetailRow label="Início Vigência" value={formatDateDdMmYyyy(selectedInvoice.dataIniVigenciaFatObt)} ariaLabel="Início vigência" />
                  <DetailRow label="Fim Vigência" value={formatDateDdMmYyyy(selectedInvoice.dataFimVigenciaFatObt)} ariaLabel="Fim vigência" />
                  <DetailRow label="Data Emissão" value={formatDateDdMmYyyy(selectedInvoice.dataEmissaoFatura)} ariaLabel="Data emissão" />
                  <DetailRow label="Data Cancelamento" value={formatDateDdMmYyyy(selectedInvoice.dataCancelFatura)} ariaLabel="Data cancelamento" />
                  <DetailRow label="Data Vencimento" value={formatDateDdMmYyyy(selectedInvoice.dataVencFatura)} ariaLabel="Data vencimento" />
                  <DetailRow label="Data Pagamento" value={formatDateDdMmYyyy(selectedInvoice.dataPagFatura)} ariaLabel="Data pagamento" />

                </div>
              </div>

              {/* Fixed button */}
              <div className="p-4 bottom-0 sticky bg-white mx-2">
                <Button
                  type="button"
                  color="secondary"
                  fill="outline"
                  onClick={handleCloseModal}
                  aria-label="Fechar modal de detalhamento da fatura"
                >
                  Fechar
                </Button>
              </div>
            </>
          )}
        </div>

      </BsdsModal>
    </div>
  );
};

export default PaymentTab;

